import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CheckIcon, PlusIcon, SearchLgIcon } from '@untitledui-icons/react/line'

import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/Modal'
import { cn } from '@/lib/utils'
import {
  useAssignWidgetTemplate,
  type WidgetOverviewRow,
  type WidgetTemplate,
} from '@/hooks/api/useWidgets'

interface WidgetTemplatePickerModalProps {
  row: WidgetOverviewRow | null
  templates: WidgetTemplate[]
  onClose: () => void
}

/**
 * The full template list behind the row menu's "see all". Picking a template
 * only changes styling — the assistant keeps its widget key and its number.
 */
export function WidgetTemplatePickerModal({
  row,
  templates,
  onClose,
}: WidgetTemplatePickerModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const base = 'resources.widgets.picker'
  const assignTemplate = useAssignWidgetTemplate()
  const [search, setSearch] = useState('')

  const handleClose = () => {
    setSearch('')
    onClose()
  }

  const query = search.trim().toLowerCase()
  const customerTemplates = row
    ? templates.filter((template) => template.customer_id === row.customer_id)
    : []
  const matches = query
    ? customerTemplates.filter((template) => template.name.toLowerCase().includes(query))
    : customerTemplates

  const handleSelect = async (template: WidgetTemplate) => {
    if (!row || template.id === row.template_id) {
      handleClose()
      return
    }

    try {
      await assignTemplate.mutateAsync({ assistant_id: row.assistant_id, template_id: template.id })
      toast.success(t(`${base}.assigned_toast`, { template: template.name }))
      handleClose()
    } catch {
      toast.error(t(`${base}.assign_failed_toast`))
    }
  }

  return (
    <Modal isOpen={!!row} onOpenChange={(isOpen) => !isOpen && handleClose()} size="lg">
      <ModalContent>
        <ModalHeader onClose={handleClose} bordered={false}>
          <div className="space-y-1">
            <ModalTitle>{t(`${base}.title`, { venue: row?.assistant_name ?? '' })}</ModalTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t(`${base}.subtitle`)}</p>
          </div>
        </ModalHeader>

        <ModalBody className="space-y-2">
          <Input
            type="search"
            value={search}
            onChange={setSearch}
            aria-label={t(`${base}.search_label`)}
            placeholder={t(`${base}.search_placeholder`)}
            leftIcon={<SearchLgIcon className="h-4 w-4" />}
          />

          {matches.length === 0 && (
            <p className="px-1 py-4 text-sm text-gray-500 dark:text-gray-400">
              {t(`${base}.no_matches`, { query: search.trim() })}
            </p>
          )}

          {matches.map((template) => {
            const isCurrent = template.id === row?.template_id

            return (
              <button
                key={template.id}
                type="button"
                onClick={() => void handleSelect(template)}
                disabled={assignTemplate.isPending}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors',
                  isCurrent
                    ? 'border-gray-900 dark:border-gray-100'
                    : 'border-transparent bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/60 dark:hover:bg-gray-800',
                  assignTemplate.isPending && 'cursor-not-allowed opacity-60'
                )}
              >
                <span
                  className="h-7 w-7 shrink-0 rounded-md border border-black/10"
                  style={{ backgroundColor: template.style.colors.whatsapp_button ?? undefined }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {template.name}
                  </span>
                  <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                    {t('resources.widgets.templates.assistants_count', {
                      count: template.assignments.length,
                    })}
                    {template.kind === 'multi' &&
                      ` · ${t('resources.widgets.templates.multi_venue_short')}`}
                  </span>
                </span>
                {isCurrent && (
                  <span className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                    <CheckIcon className="h-4 w-4" />
                    {t(`${base}.current`)}
                  </span>
                )}
              </button>
            )
          })}

          <button
            type="button"
            onClick={() =>
              navigate(
                `/widgets/templates/new?assign=${row?.assistant_id}&customer=${row?.customer_id}`
              )
            }
            className="flex w-full items-center gap-3 rounded-xl border border-dashed border-gray-300 p-3 text-left transition-colors hover:border-gray-400 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800/60"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <PlusIcon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t(`${base}.create_title`)}
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {t(`${base}.create_description`)}
              </span>
            </span>
          </button>
        </ModalBody>

        <ModalFooter bordered={false}>
          <Button color="secondary" size="md" onPress={handleClose}>
            {t('resources.generic.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
